import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { MagnifyingGlass, FunnelSimple } from '@phosphor-icons/react';

export default function AuditTrailPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [entityType, setEntityType] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    const params = { page, limit: 50 };
    if (entityType !== 'all') params.entity_type = entityType;
    if (search) params.search = search;
    api.get('/audit-logs', { params }).then(r => {
      setLogs(r.data.items);
      setTotal(r.data.total);
    }).catch(console.error).finally(() => setLoading(false));
  }, [page, entityType, search]);

  const ACTION_COLORS = {
    created: 'text-[#0055FF]',
    submitted: 'text-[#0055FF]',
    approved: 'text-[#166534]',
    rejected: 'text-[#991B1B]',
    updated: 'text-[#475569]',
    cancelled: 'text-[#475569]',
    archived: 'text-[#475569]',
    sap_import: 'text-[#166534]',
    attachment_added: 'text-[#0055FF]',
    file_uploaded: 'text-[#0055FF]',
  };

  return (
    <div className="space-y-4" data-testid="audit-trail-page">
      <h1 className="font-heading text-3xl font-bold tracking-tight">Audit Trail</h1>

      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search audit logs..." className="pl-9 rounded-sm h-9" data-testid="audit-search-input" />
        </div>
        <Select value={entityType} onValueChange={v => { setEntityType(v); setPage(1); }}>
          <SelectTrigger className="w-[200px] rounded-sm h-9" data-testid="audit-type-filter">
            <FunnelSimple size={14} className="mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-sm">
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="payment_request">Payment Requests</SelectItem>
            <SelectItem value="vendor">Vendors</SelectItem>
            <SelectItem value="contract">Contracts</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-[#475569]">{total} entries</span>
      </div>

      <div className="border rounded-sm bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F1F5F9]">
              <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Timestamp</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Type</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Action</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Performed By</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Remarks</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => <TableRow key={i}><TableCell colSpan={5}><div className="h-4 bg-muted rounded animate-pulse" /></TableCell></TableRow>)
            ) : logs.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center text-[#94A3B8] py-8">No audit logs found</TableCell></TableRow>
            ) : (
              logs.map(l => (
                <TableRow key={l.id} className="hover:bg-[#F8FAFC]">
                  <TableCell className="text-xs text-[#475569] font-mono">{l.created_at?.slice(0, 19).replace('T', ' ')}</TableCell>
                  <TableCell><span className="text-[10px] uppercase tracking-[0.05em] font-semibold px-1.5 py-0.5 rounded-sm bg-[#F1F5F9]">{l.entity_type}</span></TableCell>
                  <TableCell><span className={`text-sm font-medium capitalize ${ACTION_COLORS[l.action] || 'text-[#475569]'}`}>{l.action}</span></TableCell>
                  <TableCell className="text-sm">{l.performed_by_name} <span className="text-[10px] text-[#94A3B8] uppercase">{l.performed_by_role}</span></TableCell>
                  <TableCell className="text-sm text-[#475569] max-w-xs truncate">{l.remarks}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {total > 50 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" className="rounded-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <span className="text-sm py-1 px-3">Page {page} of {Math.ceil(total / 50)}</span>
          <Button variant="outline" size="sm" className="rounded-sm" disabled={page >= Math.ceil(total / 50)} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}
